import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { RootState } from "../../../../globals";
import HttpManager from "../../../../services/HttpManager";
import useEsTelefono from "../../../../utils/esTelefono";
import useEnlaceDirecto from "../../../../utils/enlaceDirecto";
import { serverError } from "../../../../components/modals/payments/register/registerPaymentModal";
import { Conversacion } from "../audits/auditsController";
import "../promotions/promotions.css";
import "../audits/audits.css";
import { fechaHoraAmPm } from "../../../../utils/fechaHoraAmPm";

/*
	Preguntas de Auditoría (Finanzas, sección 93; ESPEC_AUDITORIA_PREGUNTAS C). Lo que Auditoría (Contabilidad / Contraloría) le
	pregunta a quien aplicó un descuento o un cobro, con su supervisión en copia (Caja → Supervisión de Caja del plantel; Admisiones
	→ Supervisora y Dirección de Admisiones; y quien aprobó). Cada quien ve SOLO sus casos (lo decide el servidor). Se responde
	aquí; el caso vuelve a Auditoría como «Respondido» y la conversación completa queda en el caso.
	Pensada para el teléfono (Esteban, 2026-10-09: supervisoras y cajeras responden desde el celular): una columna, la caja de
	respuesta grande con letra de 16 px, botón de 44 px a lo ancho que se apaga mientras envía (doble toque), nada de hover.
	Enlace del Cliq: /auditQuestions?caso=ID&plantel=N abre ese caso directo (también después de iniciar sesión).
*/

const http = HttpManager.getInstance();
const fechaHora = fechaHoraAmPm;   // siempre am/pm
const ROLE_TEXT: Record<string, string> = { persona: "Te preguntan a ti", supervision: "En copia (supervisión)", aprobo: "En copia (lo aprobaste)" };

const AuditQuestionsController: React.FC = () => {
	const movil = useEsTelefono();
	const sessionCampus: any = useSelector((state: RootState) => state.rootReducer.sessionState.currentCampus);
	const legacyCampusKey: any = useSelector((state: RootState) => state.currentUserCampus.value);
	const legacyCampusRef: any = useSelector((state: RootState) => state.campusRef.value);
	const legacyCampus: any = legacyCampusKey && legacyCampusRef ? legacyCampusRef[legacyCampusKey] : null;
	const campusId: number | null = legacyCampus?.id ?? sessionCampus?.id ?? null;

	const [status, setStatus] = useState<string>("pendientes");
	const [data, setData] = useState<any>(null);
	const [error, setError] = useState<string | null>(null);
	const [ok, setOk] = useState<string | null>(null);
	const [abierto, setAbierto] = useState<any>(null);
	const [respuesta, setRespuesta] = useState<string>("");
	const [busy, setBusy] = useState<boolean>(false);
	const enviando = useRef<boolean>(false);
	const arriba = useRef<HTMLDivElement | null>(null);

	const load = () => {
		if (!campusId) return;
		http.paymentService.auditQuestions(campusId, status).then((r: any) => { setData(r.data); setError(null); })
			.catch((e: any) => setError(serverError(e)));
	};
	useEffect(() => {
		setData(null);
		load();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [campusId, status]);

	const abrir = async (id: number, forzar = false) => {
		if (!forzar && abierto && abierto.id === id) { setAbierto(null); return; }
		try {
			const r = await http.paymentService.auditQuestion(campusId!, id);
			setAbierto(r.data); setRespuesta(""); setOk(null);
		} catch (e: any) {
			// Enlace a un caso donde nadie te preguntó (o ya no eres parte): no es un error, solo no hay nada para ti.
			if (e?.response?.status === 404) { setAbierto(null); setError(null); setOk("Este caso no tiene preguntas para ti."); }
			else setError(serverError(e));
		}
	};
	const enlace = useEnlaceDirecto("caso", (id) => { abrir(id, true).then(() => setTimeout(() => (document.querySelector(".auDetalleCaso") || arriba.current)?.scrollIntoView({ block: "start" }), 250)); });

	const responder = async () => {
		if (!abierto || !campusId || enviando.current) return;
		if (respuesta.trim().length < 10) { setError("Escribe la respuesta (al menos 10 caracteres)."); return; }
		enviando.current = true;
		setBusy(true);
		try {
			const r = await http.paymentService.answerAudit(campusId, abierto.id, respuesta.trim());
			setAbierto(r.data); setRespuesta(""); setError(null); setOk("Respuesta enviada a Auditoría.");
			load();
		} catch (e) {
			setError(serverError(e));
		} finally {
			enviando.current = false;
			setBusy(false);
		}
	};

	if (!campusId) return <div className="container-fluid waModulo waDescuentos" style={{ padding: 24 }}>Elige un plantel.</div>;

	const detalle = (a: any) => (
		<div className="wdDetalle auDetalleCaso">
			<div className="auDetalleCabeza"><b>{a.reasonName}</b> · caso #{a.id} <span className={"wdChip " + (a.statusId === 5 ? "wdChipOro" : "wdChipFilo")}>{a.statusId === 5 ? "Esperan Tu Respuesta" : a.status}</span></div>
			<div className="wdTenue" style={{ fontSize: 14 }}>{a.campusName}{a.student ? " · " + a.student.name + (a.student.identityCode ? " · " + a.student.identityCode : "") : ""}</div>
			<div className="auTexto">{a.details}</div>
			<div className="wdTenue" style={{ fontSize: 13 }}>{a.createdBy ? "Aplicó: " + a.createdBy : ""}{a.approvedBy ? " · Aprobó: " + a.approvedBy : ""}{a.screen ? " · " + a.screen : ""}</div>
			<Conversacion history={a.conversation || []} />
			{a.canAnswer ? (
				<div className="auAcciones">
					<label htmlFor={"resp" + a.id} style={{ fontWeight: 700, fontSize: 14 }}>Tu Respuesta</label>
					<textarea id={"resp" + a.id} className="form-control auCaja" rows={movil ? 5 : 3} placeholder="Explica qué pasó (al menos 10 caracteres)" value={respuesta} disabled={busy}
						onChange={(e) => setRespuesta(e.target.value)} />
					<div className="auBotones">
						<button type="button" className="wdBoton wdPrimario" disabled={busy || respuesta.trim().length < 10} onClick={responder}>{busy ? "Enviando…" : "Enviar Respuesta"}</button>
					</div>
				</div>
			) : <div className="wdTenue" style={{ fontSize: 13, marginTop: 6 }}>{a.statusId === 6 ? "Ya respondieron: Auditoría lo revisa." : "Este caso ya no espera respuesta."}</div>}
		</div>
	);

	const enLista = abierto && data && data.data.some((a: any) => a.id === abierto.id);

	return (
		<div ref={arriba} className="container-fluid waModulo waDescuentos waAuditoria" style={{ padding: movil ? "10px 12px 24px 12px" : "10px 24px 32px 24px" }}>
			<div className="wdCabeza">
				<h4 className="wdTitulo">Preguntas de Auditoría</h4>
				{data ? <span className="wdTenue">{data.count ? data.count + " por responder" : "Nada por responder"}</span> : null}
			</div>
			{enlace.aviso ? <div className="wdAviso" role="alert" onClick={enlace.cerrar}>{enlace.aviso}</div> : null}
			{error ? <div className="wdAviso" role="alert" onClick={() => setError(null)}>{error}</div> : null}
			{ok ? <div className="wdNota" role="status" onClick={() => setOk(null)}>{ok}</div> : null}
			<div className="wdTabs">
				<button type="button" className={"wdTab" + (status === "pendientes" ? " wdTabOn" : "")} onClick={() => { setStatus("pendientes"); setAbierto(null); }}>Por Responder</button>
				<button type="button" className={"wdTab" + (status === "todas" ? " wdTabOn" : "")} onClick={() => { setStatus("todas"); setAbierto(null); }}>Todas</button>
			</div>
			{abierto && data && !enLista ? detalle(abierto) : null}
			{data === null ? <div className="loader" /> : !data.data.length ? <div className="wdTenue">{status === "pendientes" ? "No tienes preguntas por responder." : "Nada aquí."}</div> : data.data.map((a: any) => (
				<React.Fragment key={"q" + a.id}>
					<button type="button" className="auFila" onClick={() => abrir(a.id)}>
						<div><span className={"wdChip " + (a.statusId === 5 ? "wdChipOro" : "wdChipFilo")}>{a.statusId === 5 ? "Por Responder" : a.status}</span></div>
						<div className="auMotivo"><b>{a.reasonName}</b><small>{[a.student?.name, a.campusName].filter(Boolean).join(" · ")}</small></div>
						<div className="auDetalle">{a.lastQuestion || a.details}</div>
						<div className="wdTenue" style={{ fontSize: 12 }}>{ROLE_TEXT[a.myRole] || ""}<br />{fechaHora(a.askedAt)}</div>
					</button>
					{abierto && abierto.id === a.id ? detalle(abierto) : null}
				</React.Fragment>
			))}
		</div>
	);
};

export default AuditQuestionsController;
